import React from 'react';
import { Chip } from '@mui/material';
import { formatEnumLabel } from '../constants/enums';

export const chipSx = {
  fontWeight: 700,
  borderRadius: '999px',
  height: 26,
};

export const StatusChip = ({ label, color = 'default', variant = 'filled' }) => {
  if (!label || label === '—') return '—';
  return (
    <Chip
      size="small"
      color={color}
      variant={variant}
      label={label}
      sx={chipSx}
    />
  );
};

const upper = (value) => String(value || '').trim().toUpperCase();

export const riskPriorityColor = (value) => {
  switch (upper(value)) {
    case 'CRITICAL':
      return 'error';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'info';
    case 'LOW':
      return 'success';
    default:
      return 'default';
  }
};

export const resolutionStatusColor = (value) => {
  switch (upper(value)) {
    case 'OPEN':
    case 'OVERDUE':
      return 'warning';
    case 'IN_PROGRESS':
      return 'info';
    case 'RESOLVED':
    case 'CLOSED':
    case 'COMPLETED':
    case 'MITIGATED':
    case 'ACCEPTED':
      return 'success';
    case 'FAILED':
    case 'ROLLED_BACK':
      return 'error';
    case 'MONITORING':
    case 'PLANNED':
      return 'secondary';
    default:
      return 'default';
  }
};

export const approvalStatusColor = (value) => {
  const key = upper(value).replace(/[\s_-]+/g, '');
  if (key === 'APPROVED' || key === 'FULLYAPPROVED' || key === 'CONDITIONAL') return 'success';
  if (key === 'DECLINED' || key === 'REJECTED') return 'error';
  if (key === 'PENDING' || key.startsWith('AWAITING')) return 'warning';
  if (key === 'NONE' || !key) return 'default';
  return 'info';
};

export const deploymentStatusColor = (value) => {
  switch (upper(value)) {
    case 'PRODUCTION':
      return 'success';
    case 'PILOT':
    case 'TESTING':
      return 'info';
    case 'DEVELOPMENT':
    case 'PLANNED':
      return 'secondary';
    case 'SUSPENDED':
      return 'warning';
    case 'RETIRED':
      return 'default';
    default:
      return 'default';
  }
};

export const decisionTypeColor = (value) => {
  switch (upper(value)) {
    case 'FULLY_AUTOMATED':
      return 'error';
    case 'SEMI_AUTOMATED':
      return 'warning';
    case 'ADVISORY':
      return 'info';
    default:
      return 'default';
  }
};

export const deploymentEnvironmentColor = (value) => {
  switch (upper(value)) {
    case 'CLOUD':
      return 'info';
    case 'ON_PREMISES':
      return 'secondary';
    case 'HYBRID':
      return 'warning';
    default:
      return 'default';
  }
};

export const changeTypeColor = (value) => {
  switch (upper(value)) {
    case 'EMERGENCY':
      return 'error';
    case 'NORMAL':
      return 'warning';
    case 'STANDARD':
      return 'success';
    default:
      return 'default';
  }
};

export const approvalLevelColor = (value) => {
  switch (upper(value)) {
    case 'EXECUTIVE':
      return 'error';
    case 'BUSINESS':
      return 'warning';
    case 'COMPLIANCE':
      return 'info';
    case 'TECHNICAL':
      return 'secondary';
    default:
      return 'default';
  }
};

export const recordStatusColor = (value) => {
  const key = upper(value);
  if (key === 'DELETED') return 'error';
  if (key === 'ACTIVE') return 'success';
  return 'default';
};

const enumChip =
  (colorFn, { format = true } = {}) =>
  ({ value, row }) => {
    const raw = value ?? '';
    const label = format ? (formatEnumLabel(raw) || String(raw || '').trim()) : String(raw || '').trim();
    return <StatusChip label={label} color={colorFn(raw)} />;
  };

/** Prefer raw enum on row when display value is already formatted. */
const enumChipFromRow =
  (field, colorFn) =>
  ({ value, row }) => {
    const raw = row?.[field] ?? value ?? '';
    const label = value ? String(value) : formatEnumLabel(raw);
    return <StatusChip label={label || formatEnumLabel(raw)} color={colorFn(raw)} />;
  };

export const renderRiskRatingChip = enumChip(riskPriorityColor);
export const renderPriorityChip = enumChip(riskPriorityColor);
export const renderResolutionStatusChip = enumChip(resolutionStatusColor);
export const renderMonitoringStatusChip = enumChip(resolutionStatusColor);
export const renderImplementationStatusChip = enumChip(resolutionStatusColor);
export const renderSoftwareRiskStatusChip = enumChip(resolutionStatusColor);
export const renderApprovalStatusChip = ({ value }) => (
  <StatusChip label={value || ''} color={approvalStatusColor(value)} />
);
export const renderApprovalStatusEnumChip = enumChip(approvalStatusColor);
export const renderDeploymentStatusChip = enumChip(deploymentStatusColor);
export const renderDeploymentStatusLabelChip = enumChipFromRow('status', deploymentStatusColor);
export const renderDecisionChip = ({ value }) => (
  <StatusChip label={value ? formatEnumLabel(value) || value : ''} color={approvalStatusColor(value)} />
);
export const renderDecisionTypeChip = enumChip(decisionTypeColor);
export const renderDeploymentEnvironmentChip = enumChip(deploymentEnvironmentColor);
export const renderChangeTypeChip = enumChip(changeTypeColor);
export const renderApprovalLevelChip = enumChip(approvalLevelColor);
export const renderRecordStatusChip = ({ value, row }) => {
  const raw = value || row?.status || '';
  const deleted = upper(raw) === 'DELETED';
  return (
    <StatusChip
      label={raw || 'ACTIVE'}
      color={recordStatusColor(raw)}
      variant="filled"
    />
  );
};
export const renderCurrentChip = ({ value }) => (
  <StatusChip
    label={value ? 'Current' : 'No'}
    color={value ? 'success' : 'default'}
    variant={value ? 'filled' : 'outlined'}
  />
);
export const renderEnumChip = ({ value }) => (
  <StatusChip label={value ? formatEnumLabel(value) : ''} color="default" />
);
